"use client";

import { Suspense, useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import axios from "axios";
import { useAuthStore } from "@/store/auth";
import CategoryFilterChips from "@/components/ui/CategoryFilterChips";
import { TrendingSection } from "@/components/manufacturers/TrendingSection";

// 2026-05-18 — 공급처별 카드 → 제품 단위 flat-list 로 전환 (사용자 결정 Q1=C / Q4=제거).
// 백엔드: GET /api/manufacturers/products (마이그 079 product_url 사용).
// 제품 행 클릭 → product_url 새 탭, 공급처명 클릭 → manufacturer_id 필터.

interface ProductFlatOut {
  id: number;
  product_name: string;
  category: string | null;
  spec: string | null;
  product_url: string | null;
  manufacturer_id: number;
  manufacturer_company: string;
}

interface ProductFlatListResponse {
  items: ProductFlatOut[];
  total: number;
  page: number;
  size: number;
  has_more: boolean;
  locked?: boolean;   // free_auth (베타 종료 후) cap 도달 — 잠금 CTA 노출
}

interface CategoryOut {
  category: string;
  count: number;
}

const PAGE_SIZE = 50;

// 비로그인 데모 — 제품 단위 (2026-05-18 reshape)
const LOCKED_DEMO_PRODUCTS = [
  { name: "서울우유 밀크마스터 1L", cat: "유제품", mfr: "○○ 유통" },
  { name: "발로나 다크 70% 1kg", cat: "재료·원료", mfr: "△△ 베이킹" },
  { name: "프리미엄 크루아상 생지 60g (50ea)", cat: "베이커리", mfr: "□□ 베이커리" },
  { name: "딸기 시럽 1L (무방부제)", cat: "재료·원료", mfr: "◇◇ 시럽" },
  { name: "마카롱 OEM (10ea 부터)", cat: "디저트", mfr: "☆☆ 디저트랩" },
  { name: "치즈·생크림 직납 (200g~)", cat: "유제품", mfr: "○△ 유제품 농장" },
];

export default function ManufacturersPage() {
  return (
    <Suspense fallback={<p className="text-text-sub text-center py-8">불러오는 중...</p>}>
      <ManufacturersInner />
    </Suspense>
  );
}

function ManufacturersInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { token, isLoggedIn } = useAuthStore();
  const hasHydrated = useAuthStore((s) => s._hasHydrated);

  const initialQ = searchParams.get("q") ?? "";
  const initialCategory = searchParams.get("category") ?? "";

  const [searchInput, setSearchInput] = useState(initialQ);
  const [q, setQ] = useState(initialQ);
  const [category, setCategory] = useState(initialCategory);

  const [categories, setCategories] = useState<CategoryOut[]>([]);
  const [data, setData] = useState<ProductFlatListResponse | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 2026-05-19 — 검색어 변경 시 카테고리 chip 카운트도 동기 갱신 (사용자 지적)
  useEffect(() => {
    if (!hasHydrated || !isLoggedIn || !token) return;
    const headers = { Authorization: `Bearer ${token}` };
    const params: Record<string, string> = {};
    if (q.trim()) params.q = q.trim();
    axios
      .get<CategoryOut[]>("/api/manufacturers/categories", { headers, params })
      .then((r) => setCategories(r.data))
      .catch(() => setCategories([]));
  }, [hasHydrated, isLoggedIn, token, q]);

  useEffect(() => {
    if (!hasHydrated) return;
    if (!isLoggedIn || !token) {
      setLoading(false);
      setError("로그인이 필요합니다.");
      return;
    }
    setLoading(true);
    setError(null);
    setPage(1);
    const headers = { Authorization: `Bearer ${token}` };
    const params: Record<string, string | number> = { page: 1, size: PAGE_SIZE };
    if (q.trim()) params.q = q.trim();
    if (category) params.category = category;

    axios
      .get<ProductFlatListResponse>("/api/manufacturers/products", { headers, params })
      .then((r) => setData(r.data))
      .catch((err) => {
        setData(null);
        if (err?.response?.status === 403) setError("🔒 로그인이 필요해요");
        else setError("불러오기 실패");
      })
      .finally(() => setLoading(false));
  }, [q, category, hasHydrated, isLoggedIn, token]);

  const loadMore = async () => {
    if (!data || loadingMore) return;
    setLoadingMore(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const next = page + 1;
      const params: Record<string, string | number> = { page: next, size: PAGE_SIZE };
      if (q.trim()) params.q = q.trim();
      if (category) params.category = category;
      const r = await axios.get<ProductFlatListResponse>("/api/manufacturers/products", {
        headers,
        params,
      });
      setData({ ...r.data, items: [...data.items, ...r.data.items] });
      setPage(next);
    } finally {
      setLoadingMore(false);
    }
  };

  const syncUrl = (overrides?: { q?: string; category?: string }) => {
    const params = new URLSearchParams();
    const qv = overrides?.q ?? q;
    const cv = overrides?.category ?? category;
    if (qv && qv.trim()) params.set("q", qv.trim());
    if (cv) params.set("category", cv);
    const qs = params.toString();
    router.replace(qs ? `/manufacturers?${qs}` : "/manufacturers");
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setQ(searchInput);
    syncUrl({ q: searchInput });
  };

  const changeCategory = (cat: string) => {
    setCategory(cat);
    syncUrl({ category: cat });
  };

  // 엑셀(CSV) 다운로드 — 현재 검색/카테고리 필터 그대로 내보내기 (UTF-8 BOM, 엑셀 바로 열림)
  const [downloading, setDownloading] = useState(false);
  const downloadExcel = async () => {
    if (!token || downloading) return;
    setDownloading(true);
    try {
      const params: Record<string, string> = {};
      if (q.trim()) params.q = q.trim();
      if (category) params.category = category;
      const r = await axios.get("/api/manufacturers/products/export", {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      let fname = "trendrain_products.csv";
      const cd = (r.headers["content-disposition"] as string) || "";
      const m = /filename\*=UTF-8''([^;]+)/.exec(cd);
      if (m) {
        try { fname = decodeURIComponent(m[1]); } catch { /* keep default */ }
      }
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: unknown) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 429) alert("다운로드가 너무 잦아요. 잠시 후 다시 시도해 주세요.");
      else if (status === 400) alert("검색 결과만 다운로드할 수 있어요. 검색어를 입력해 주세요.");
      else alert("다운로드에 실패했어요. 잠시 후 다시 시도해 주세요.");
    } finally {
      setDownloading(false);
    }
  };

  // 비로그인 — 제품 데모 그리드 + blur 처리
  if (hasHydrated && !isLoggedIn) {
    return (
      <div className="px-4 py-6">
        <header className="mb-5 flex items-center justify-between gap-2">
          <div>
            <h1 className="text-xl font-black text-text">공급처 제품</h1>
            <p className="text-2xs text-text-sub mt-1">🔒 로그인하면 전체 제품 검색 + 카테고리 필터</p>
          </div>
          <Link href="/login" className="text-xs font-semibold px-3 py-2 rounded-lg bg-primary text-bg shrink-0">
            로그인 →
          </Link>
        </header>

        <div className="mb-4">
          <TrendingSection />
        </div>

        <div className="space-y-2">
          {LOCKED_DEMO_PRODUCTS.map((p, i) => (
            <div key={i} className="card">
              <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                <span className="text-2xs font-bold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">
                  {p.cat}
                </span>
              </div>
              <p className="text-sm font-bold text-text">{p.name}</p>
              <p className="text-2xs text-text-sub mt-1 blur-sm select-none">공급처 · {p.mfr}</p>
            </div>
          ))}
        </div>
        <Link href="/login" className="block mt-5 btn-primary text-center text-sm py-3">
          로그인하고 시작하기
        </Link>
      </div>
    );
  }

  return (
    <div className="pb-24">
      {/* 헤더 */}
      <div className="sticky top-0 z-40 bg-bg/90 backdrop-blur-md border-b border-border px-4 py-3">
        <h1 className="text-lg font-black text-text">공급처 제품</h1>
      </div>

      {/* 인기 공급처 차트 — 보조 정보 (Q1=C, 공급처는 보조) */}
      <div className="px-4 mt-3">
        <TrendingSection />
      </div>

      {/* 검색 + 제품명 autocomplete */}
      <SearchWithSuggest
        token={token}
        value={searchInput}
        onChange={(v) => {
          setSearchInput(v);
          if (v === "" && q !== "") {
            setQ("");
            syncUrl({ q: "" });
          }
        }}
        onSubmit={(v) => {
          setSearchInput(v);
          setQ(v);
          syncUrl({ q: v });
        }}
      />

      {/* 카테고리 필터 */}
      <div className="px-4 mt-3">
        <CategoryFilterChips
          useUrlQuery={false}
          value={category}
          onChange={(c) => changeCategory(c)}
          counts={Object.fromEntries(categories.map((row) => [row.category, row.count]))}
        />
      </div>

      {/* 제품 리스트 */}
      <div className="px-4 mt-4">
        {loading ? (
          <p className="text-text-sub text-center py-8">불러오는 중...</p>
        ) : error ? (
          <p className="text-text-sub text-center py-8">{error}</p>
        ) : !data || data.items.length === 0 ? (
          <div className="text-center py-10 space-y-2">
            <p className="text-text-sub text-sm">
              {q ? <>&quot;{q}&quot; 검색 결과가 없어요</> : "조건에 맞는 제품이 없어요"}
            </p>
            {q && <p className="text-2xs text-text-sub/70">다른 검색어로 시도해 보세요</p>}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2 mb-3">
              <p className="text-xs text-text-sub min-w-0">
                {q ? (
                  <>
                    <span className="text-text">&quot;{q}&quot;</span> 검색 결과
                    {category && <> · {category}</>} ·{" "}
                    <span className="text-text font-semibold">{data.total}</span>개
                  </>
                ) : (
                  <>
                    {category || "전체"} <span className="text-text font-semibold">{data.total}</span>개
                  </>
                )}
              </p>
              {q.trim() && data.total > 0 && (
                <button
                  onClick={downloadExcel}
                  disabled={downloading}
                  className="shrink-0 text-2xs font-semibold px-2.5 py-1.5 rounded-md border border-primary/40 text-primary hover:bg-primary/5 disabled:opacity-50"
                  title="검색 결과를 엑셀(CSV)로 저장"
                >
                  {downloading ? "내보내는 중..." : "⬇ 검색결과 엑셀"}
                </button>
              )}
            </div>
            <div className="border border-border rounded-lg overflow-hidden">
              {/* 헤더 (sticky X — 표 상단 고정만, 3컬럼) */}
              <div className="grid grid-cols-[2fr_1fr_38px] gap-1.5 px-2 py-1.5 text-[10px] font-bold text-text-sub bg-bg-elevated border-b border-border">
                <span>제품</span>
                <span>공급처</span>
                <span className="text-center">이동</span>
              </div>
              {/* 행들 */}
              {data.items.map((p) => (
                <ProductRow key={p.id} product={p} />
              ))}
            </div>
            {data.has_more && (
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="w-full mt-2 py-3 text-sm font-semibold text-text-sub border border-border rounded-2xl hover:bg-bg-elevated disabled:opacity-50"
              >
                {loadingMore ? "불러오는 중..." : "더 보기"}
              </button>
            )}
            {/* free_auth (베타 종료 후) cap 도달 — 잠금 CTA (사용자 결정 2026-05-22) */}
            {!data.has_more && data.locked && (
              <Link
                href="/mypage/subscribe"
                className="mt-2 flex flex-col items-center justify-center gap-1 py-4 rounded-xl bg-primary/10 border border-primary/30 hover:bg-primary/15 transition-colors"
              >
                <span className="text-sm font-bold text-primary">
                  🔒 구독하면 {(data.total - data.items.length).toLocaleString()}개 더 보기
                </span>
                <span className="text-2xs text-text-sub">
                  전체 <span className="font-bold text-text">{data.total.toLocaleString()}</span>개 중 {data.items.length}개 표시 중
                </span>
              </Link>
            )}
            {!data.has_more && !data.locked && data.items.length > PAGE_SIZE && (
              <p className="text-2xs text-text-sub text-center pt-2">더 이상 없어요</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ProductRow({ product }: { product: ProductFlatOut }) {
  const hasUrl = !!product.product_url;
  const onMove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasUrl) window.open(product.product_url!, "_blank", "noopener,noreferrer");
  };
  // 제품명 + 규격 inline 조합 — 규격 별도 있으면 ", 1L" 형태로 부착
  const productLabel =
    product.spec && !product.product_name.includes(product.spec)
      ? `${product.product_name}, ${product.spec}`
      : product.product_name;
  return (
    <div className="grid grid-cols-[2fr_1fr_38px] gap-1.5 items-center px-2 py-2 text-xs border-b border-border last:border-b-0">
      {/* 제품 (name + spec 통합) */}
      <span className="font-semibold text-text truncate" title={productLabel}>
        {productLabel}
      </span>
      {/* 공급처 (필터 X, 단순 텍스트) */}
      <span className="text-2xs text-text-sub truncate" title={product.manufacturer_company}>
        {product.manufacturer_company}
      </span>
      {/* 이동 — URL 있을 때만 클릭 */}
      {hasUrl ? (
        <button
          onClick={onMove}
          className="text-[10px] font-bold text-primary hover:underline text-center"
          title="새 탭에서 제품 페이지 열기"
        >
          이동 ↗
        </button>
      ) : (
        <span className="text-[10px] text-text-sub/40 text-center">-</span>
      )}
    </div>
  );
}

// 2026-05-18 — 제품명 autocomplete (debounced typeahead).
function SearchWithSuggest({
  token,
  value,
  onChange,
  onSubmit,
}: {
  token: string | null;
  value: string;
  onChange: (v: string) => void;
  onSubmit: (v: string) => void;
}) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const debounceRef = useState<{ id: NodeJS.Timeout | null }>({ id: null })[0];

  useEffect(() => {
    if (!value || value.trim().length < 1 || !token) {
      setSuggestions([]);
      return;
    }
    if (debounceRef.id) clearTimeout(debounceRef.id);
    debounceRef.id = setTimeout(() => {
      axios
        .get<{ text: string }[]>("/api/manufacturers/suggest", {
          headers: { Authorization: `Bearer ${token}` },
          params: { q: value.trim(), limit: 8 },
        })
        .then((r) => setSuggestions(r.data.map((s) => s.text)))
        .catch(() => setSuggestions([]));
    }, 200);
    return () => {
      if (debounceRef.id) clearTimeout(debounceRef.id);
    };
  }, [value, token]);

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault();
    setOpen(false);
    onSubmit(value);
  };

  // 모바일 친화 — touchstart 직후 즉시 suggestion 적용 (input blur 전에)
  const pickSuggestion = (s: string) => {
    setOpen(false);
    onChange(s);
    onSubmit(s);
  };

  return (
    <div className="px-4 mt-3 relative">
      <form onSubmit={submit} className="flex gap-2">
        <div className="relative flex-1">
          <input
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}                            // suggestions 없어도 open (다음 fetch 받으면 표시)
            onBlur={() => setTimeout(() => setOpen(false), 300)}     // 모바일 tap 충분 시간 (150 → 300)
            placeholder="제품 검색 (예: 버터떡, 우베파우더, 1L)"
            className="w-full bg-bg-secondary text-text text-sm pl-10 pr-3 py-2.5 rounded-full border border-border outline-none focus:border-primary/50"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-sub text-sm">🔍</span>
        </div>
        <button
          type="submit"
          className="bg-primary text-bg text-sm font-bold px-4 py-2.5 rounded-full whitespace-nowrap shrink-0 active:scale-95 transition-transform"
        >
          검색
        </button>
      </form>
      {/* dropdown */}
      {open && suggestions.length > 0 && (
        <div className="absolute left-4 right-4 mt-1 bg-bg-elevated border border-border rounded-xl shadow-lg z-20 max-h-72 overflow-y-auto">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              // 데스크탑: mousedown 으로 input blur 전 처리, 모바일: touchstart 동일 패턴
              onMouseDown={(e) => { e.preventDefault(); pickSuggestion(s); }}
              onTouchStart={(e) => { e.preventDefault(); pickSuggestion(s); }}
              className="block w-full text-left px-3 py-2 text-xs text-text hover:bg-bg-secondary border-b border-border last:border-b-0 truncate"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
